import { useTranslation } from 'react-i18next'

import { BadgeList } from '../display/BadgeList'
import { ConfigRow } from '../display/ConfigRow'
import { ConfigSection } from '../display/ConfigSection'
import { EditableConfigRow } from '../display/EditableConfigRow'
import { FeaturedList } from '../display/FeaturedList'
import type { ConfigQueryResult } from '../PartnerConfig.types'
import { ConfirmToggleModal } from '../confirmToggleModal/ConfirmToggleModal'

import { Flex, FormField, TextInput, Typography } from '@percent/lemonade'
import type {
  DonationRestrictedCountriesConfigValue,
  GivingFeaturedConfigValue,
  GivingHelpLinkConfigValue,
  GivingSpotlightedOrgsConfigValue
} from '@percent/admin-dashboard/api/types'
import { BooleanToggleRow } from './behaviour/BooleanToggleRow'
import { useBehaviourSection } from './behaviour/useBehaviourSection'

type BehaviourSectionProps = {
  helpLink: ConfigQueryResult<GivingHelpLinkConfigValue>
  requireOrganisationTimeLog: ConfigQueryResult<boolean>
  restrictedCountriesForDonation: ConfigQueryResult<DonationRestrictedCountriesConfigValue>
  spotlightedOrgs: ConfigQueryResult<GivingSpotlightedOrgsConfigValue>
  featured: ConfigQueryResult<GivingFeaturedConfigValue>
  isSaving: boolean
  savingKey: string | null
  mutationError?: string
  resetSaving: () => void
  saveHelpLink: (value: GivingHelpLinkConfigValue, onSuccess?: () => void) => void
  saveRequireOrganisationTimeLog: (value: boolean, onSuccess?: () => void) => void
}

export function BehaviourSection({
  helpLink,
  requireOrganisationTimeLog,
  restrictedCountriesForDonation,
  spotlightedOrgs,
  featured,
  isSaving,
  savingKey,
  mutationError,
  resetSaving,
  saveHelpLink,
  saveRequireOrganisationTimeLog
}: BehaviourSectionProps) {
  const { t } = useTranslation()

  const {
    editingKey,
    startEditing,
    cancel,
    errorForKey,
    pendingToggle,
    setPendingToggle,
    handleBooleanToggle,
    helpLinkUrlInvalid,
    showHelpLinkUrlError,
    setHelpLinkUrlTouched,
    helpLinkTitleInvalid,
    showHelpLinkTitleError,
    setHelpLinkTitleTouched,
    helpLinkDraft,
    setHelpLinkDraft
  } = useBehaviourSection({
    helpLinkData: helpLink.data,
    savingKey,
    isSaving,
    mutationError,
    resetSaving
  })

  return (
    <ConfigSection title={t('partnerConfig.section.behaviour')}>
      <BooleanToggleRow
        id="behaviour.requireTimeLog"
        label={t('partnerConfig.behaviour.requireOrganisationTimeLog')}
        tooltip={t('partnerConfig.tooltip.requireOrganisationTimeLog')}
        isLoading={requireOrganisationTimeLog.isLoading}
        isSaving={isSaving}
        error={requireOrganisationTimeLog.error}
        enabled={requireOrganisationTimeLog.data ?? false}
        onToggle={() =>
          handleBooleanToggle(
            'require_organisation_time_log',
            t('partnerConfig.behaviour.requireOrganisationTimeLog'),
            requireOrganisationTimeLog.data,
            saveRequireOrganisationTimeLog
          )
        }
      />

      <EditableConfigRow
        id="behaviour.helpLink"
        label={t('partnerConfig.behaviour.helpLink')}
        tooltip={t('partnerConfig.tooltip.helpLink')}
        isLoading={helpLink.isLoading}
        error={helpLink.error}
        isEditing={editingKey === 'helpLink'}
        isSaving={savingKey === 'help_link' && isSaving}
        saveDisabled={isSaving || helpLinkUrlInvalid || helpLinkTitleInvalid}
        saveError={errorForKey('help_link')}
        onEdit={() => startEditing('helpLink')}
        onCancel={cancel}
        onSave={() => saveHelpLink(helpLinkDraft, cancel)}
        displayContent={
          helpLink.data ? (
            <Flex direction="column" gap={1} align="flex-end">
              <Typography variant="default">{helpLink.data.title}</Typography>
              <Typography variant="small" $color="gray500">
                {helpLink.data.link}
              </Typography>
            </Flex>
          ) : undefined
        }
        editContent={
          <Flex direction="column" gap={2}>
            <FormField
              label={t('partnerConfig.behaviour.helpLink.title')}
              status={showHelpLinkTitleError ? 'danger' : 'default'}
              statusMessage={showHelpLinkTitleError ? t('partnerConfig.behaviour.helpLink.titleRequired') : undefined}
            >
              <TextInput
                name="helpLinkTitle"
                value={helpLinkDraft.title}
                status={showHelpLinkTitleError ? 'danger' : 'default'}
                onChange={event => {
                  setHelpLinkTitleTouched(true)
                  setHelpLinkDraft(prev => ({ ...prev, title: event.target.value }))
                }}
                placeholder={t('partnerConfig.behaviour.helpLink.titlePlaceholder')}
              />
            </FormField>
            <FormField
              label={t('partnerConfig.behaviour.helpLink.url')}
              status={showHelpLinkUrlError ? 'danger' : 'default'}
              statusMessage={showHelpLinkUrlError ? t('partnerConfig.behaviour.helpLink.invalidUrl') : undefined}
            >
              <TextInput
                name="helpLinkUrl"
                value={helpLinkDraft.link}
                status={showHelpLinkUrlError ? 'danger' : 'default'}
                onChange={event => {
                  setHelpLinkUrlTouched(true)
                  setHelpLinkDraft(prev => ({ ...prev, link: event.target.value }))
                }}
                placeholder={t('partnerConfig.behaviour.helpLink.urlPlaceholder')}
              />
            </FormField>
          </Flex>
        }
      />

      <ConfigRow
        id="behaviour.restrictedCountries"
        label={t('partnerConfig.behaviour.restrictedCountriesForDonation')}
        tooltip={t('partnerConfig.tooltip.restrictedCountriesForDonation')}
        isLoading={restrictedCountriesForDonation.isLoading}
        error={restrictedCountriesForDonation.error}
      >
        {restrictedCountriesForDonation.data?.blockedRegions.length ? (
          <BadgeList
            items={restrictedCountriesForDonation.data.blockedRegions.map(region =>
              region.state ? `${region.countryCode} (${region.state})` : region.countryCode
            )}
          />
        ) : undefined}
      </ConfigRow>

      <ConfigRow
        id="behaviour.spotlightedOrgs"
        label={t('partnerConfig.behaviour.spotlightedOrgs')}
        tooltip={t('partnerConfig.tooltip.spotlightedOrgs')}
        isLoading={spotlightedOrgs.isLoading}
        error={spotlightedOrgs.error}
      >
        {spotlightedOrgs.data?.organisationIds.length ? (
          <BadgeList items={spotlightedOrgs.data.organisationIds} />
        ) : undefined}
      </ConfigRow>

      <ConfigRow
        id="behaviour.featured"
        label={t('partnerConfig.behaviour.featured')}
        tooltip={t('partnerConfig.tooltip.featured')}
        isLoading={featured.isLoading}
        error={featured.error}
      >
        {featured.data?.length ? <FeaturedList items={featured.data} /> : undefined}
      </ConfigRow>

      {pendingToggle && (
        <ConfirmToggleModal
          isOpen
          label={pendingToggle.label}
          newEnabled={pendingToggle.newEnabled}
          isLoading={savingKey === pendingToggle.key && isSaving}
          errorMessage={savingKey === pendingToggle.key && !isSaving ? mutationError : undefined}
          onConfirm={pendingToggle.onConfirm}
          onClose={() => {
            setPendingToggle(null)
            resetSaving()
          }}
        />
      )}
    </ConfigSection>
  )
}
